<template>
  <div>
    <p class="mt-2">
      {{ t('warehouse.pages.settings.messages.enableToSelectAndOrderDetailsInAppointmentTile') }}
    </p>

    <v-switch v-model="enabled" color="secondary" density="compact">
      <template v-slot:label>
        <div :class="{ 'text--disabled': !enabled }">
          <div class="pl-1">
            <span>
              {{
                enabled
                  ? t('warehouse.pages.settings.messages.enabledShowingCustomGrid')
                  : t('warehouse.pages.settings.messages.disabledShowingDefaultGrid')
              }}
            </span>
          </div>
        </div>
      </template>
    </v-switch>

    <p :class="{ 'text--disabled': !enabled }">
      {{
        t(
          'warehouse.pages.settings.messages.selectAndOrderDetailsYouWantToDisplayInEachAppointmentTile'
        )
      }}:
    </p>

    <v-alert border="start" type="info" v-if="enabled && lastItem">
      {{ t('warehouse.pages.settings.messages.youMustKeepAtLeastOneItemSelected') }}
    </v-alert>

    <!-- ACTIVE ITEMS: -->
    <div id="datablock-list">
      <v-checkbox
        v-for="key in config"
        v-model="config"
        :key="key"
        :value="key"
        hide-details
        :disabled="!enabled || lastItem">
        <template v-slot:prepend>
          <v-icon class="handle">mdi-arrow-split-horizontal</v-icon>
        </template>
        <template v-slot:label>
          <grid-tile-data-block-checkbox-label :item="dataBlocks[key]" :disabled="!enabled" />
        </template>
      </v-checkbox>
    </div>

    <!-- INACTIVE ITEMS: -->
    <div>
      <v-checkbox
        v-for="(item, key) in dataBlocks"
        v-model="config"
        :key="key"
        :value="key"
        v-show="!config.includes(key)"
        hide-details
        :disabled="!enabled">
        <template v-slot:prepend>
          <v-icon class="handle" disabled>mdi-arrow-split-horizontal</v-icon>
        </template>
        <template v-slot:label>
          <grid-tile-data-block-checkbox-label :item="item" :disabled="!enabled" />
        </template>
      </v-checkbox>
    </div>

    <p class="mt-8 font-size-small">
      <span class="font-weight-bold">{{ t('global.basicTerms.notes') }}:</span>
      {{ t('warehouse.pages.settings.messages.changesWillBeAppliedToPastAndFutureAppointments') }}
    </p>
  </div>
</template>

<script>
import { computed } from 'vue';
import Sortable from 'sortablejs';
import { isArray } from 'lodash';
import { CustomFieldType, deepClone, t } from '@nova/core';
import useLimiter from '@satellite/composables/useLimiter';
import { useFlag } from '@loadsmart/vue-feature-flags';

export default {
  name: 'GridTileConfigEditor',
  setup(props) {
    const warehouseSettings = computed(() => props.warehouse?.settings);
    const { shouldShowHandlingUnits, handlingUnitsLabel } = useLimiter(warehouseSettings, useFlag);

    return {
      shouldShowHandlingUnits,
      handlingUnitsLabel
    };
  },
  props: {
    inputEnabled: {
      type: Boolean,
      required: true
    },
    inputConfig: {
      type: Array,
      required: true
    },
    warehouse: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      enabled: false,
      config: [],
      dataBlocks: {
        company_name: {
          title: this.t('warehouse.pages.settings.labels.carrierCompanyName')
        },
        start_time: {
          title: this.t('warehouse.pages.settings.labels.startTime')
        },
        tags: {
          title: this.t('warehouse.pages.settings.labels.tags')
        },
        ref_number: {
          title: this.t('warehouse.pages.settings.labels.referenceNumber')
        },
        conf_number: {
          title: this.t('warehouse.pages.settings.labels.confirmationNumber')
        },
        dock_name: {
          title: this.t('warehouse.pages.settings.labels.dockName'),
          note: this.t('warehouse.pages.settings.messages.onlyAvailableForWeekAndMonthViews')
        },
        load_type: {
          title: this.t('warehouse.pages.settings.labels.loadTypeName')
        }
      }
    };
  },
  mounted() {
    // Add handling units if feature flag and settings are enabled
    if (this.shouldShowHandlingUnits) {
      this.dataBlocks['handling_units'] = {
        title: this.handlingUnitsLabel
      };
    }

    this.enabled = this.inputEnabled;

    let config = deepClone(this.inputConfig);

    const fieldsTmplt = this.warehouse?.customApptFieldsTemplate;

    if (isArray(fieldsTmplt)) {
      for (const field of fieldsTmplt) {
        // TODO: For now, we don't allow Doc/MultiDoc custom field types.
        if (field.type !== CustomFieldType.Document && field.type !== CustomFieldType.MultiDocument)
          this.dataBlocks['custom:' + field.label] = {
            title: field.label,
            note: this.t('global.specialtyTerms.customFields')
          };
      }

      // Remove custom field items from current grid config if they don't exist
      // in the warehouse's custom fields template:
      config = config.filter(item => {
        if (item.startsWith('custom:')) {
          return item in this.dataBlocks;
        } else {
          return true;
        }
      });
    }

    this.config = config;

    setTimeout(() => this.initSortable());
  },
  computed: {
    lastItem: function () {
      return this.config.length === 1;
    }
  },
  methods: {
    t,
    initSortable() {
      const _this = this;
      const container = document.querySelector('#datablock-list');
      if (!container) {
        return;
      }

      Sortable.create(container, {
        handle: '.handle',
        animation: 150,
        filter: '.filtered',
        ghostClass: 'ghost-item',
        onEnd({ oldIndex, newIndex }) {
          const movedItem = _this.config.splice(oldIndex, 1)[0];
          _this.config.splice(newIndex, 0, movedItem);
          _this.config = [..._this.config];
        }
      });
    }
  },
  watch: {
    config: {
      handler(val) {
        this.$emit('configUpdated', deepClone(val));
      },
      deep: true
    },
    enabled: function (val) {
      if (val) {
        // If the user just switched on the new system, and nothing is selected in the config,
        // then just go ahead and select everything. This way they start out with "all the things".
        if (this.config.length === 0) {
          for (const key of Object.keys(this.dataBlocks)) {
            this.config.push(key);
          }
        }
      }
      this.$emit('enabledUpdated', deepClone(val));
    }
  }
};
</script>
